import { useEffect, useRef, useState } from 'react';
import { useCanvasStores } from '../stores/CanvasContext';

interface Size {
  width: number;
  height: number;
}

/**
 * Measures an element's size only while it is active (selected/editing).
 * Uses a ResizeObserver so content/font changes are picked up automatically
 * without any content-related deps.
 *
 * Returns [ref, measuredSize]. Attach `ref` to the DOM element to measure.
 */
export function useActiveMeasure(
  entityId: string,
  isActive: boolean,
  defaultSize: Size = { width: 60, height: 24 },
): [React.RefObject<HTMLDivElement | null>, Size] {
  const { store, measureMapRef } = useCanvasStores();
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>(defaultSize);

  useEffect(() => {
    if (!isActive) return;
    const el = ref.current;
    if (!el) return;
    const canvasEl = el.closest('[data-canvas-canvas]') as HTMLElement | null;
    const zoom = canvasEl ? parseFloat(canvasEl.dataset.zoom ?? '1') : 1;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = Math.round(entry.contentRect.width / zoom);
      const h = Math.round(entry.contentRect.height / zoom);
      setSize(prev => (prev.width === w && prev.height === h) ? prev : { width: w, height: h });
      measureMapRef.current.set(entityId, { width: w, height: h });
      store.send({ type: 'entity.updateSize', entityId, width: w, height: h });
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return [ref, size];
}
